/**
 * API Service for AMN Frontend
 * Handles all HTTP requests to the Django backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
const TRACKING_API_BASE_URL = process.env.REACT_APP_TRACKING_API_URL || 'http://localhost:8080/api/v1/admin';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = localStorage.getItem('access_token');
  }

  /**
   * Get authorization headers
   */
  getHeaders(isFormData = false) {
    const headers = {};

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response
   * @param {Function} retryFn - Function to retry the request (optional)
   */
  async handleResponse(response, retryFn = null) {
    if (response.status === 401) {
      // Token expired, try to refresh
      const refreshed = await this.refreshToken();
      if (!refreshed) {
        // Redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        throw new Error('Session expired');
      }

      // Token refreshed successfully - retry the original request
      if (retryFn) {
        return retryFn();
      }
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(data.detail || data.error || data.message || 'API request failed');
    }

    return data;
  }

  /**
   * Generic GET request
   */
  async get(endpoint, params = {}) {
    const makeRequest = async () => {
      const url = new URL(`${this.baseURL}${endpoint}`);
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
          url.searchParams.append(key, params[key]);
        }
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      return this.handleResponse(response, makeRequest);
    };

    return makeRequest();
  }

  /**
   * Generic POST request
   */
  async post(endpoint, data = {}, isFormData = false) {
    const makeRequest = async () => {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(isFormData),
        body: isFormData ? data : JSON.stringify(data),
      });

      return this.handleResponse(response, makeRequest);
    };

    return makeRequest();
  }

  /**
   * Generic PUT request
   */
  async put(endpoint, data = {}) {
    const makeRequest = async () => {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });

      return this.handleResponse(response, makeRequest);
    };

    return makeRequest();
  }

  /**
   * Generic PATCH request
   */
  async patch(endpoint, data = {}) {
    const makeRequest = async () => {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });

      return this.handleResponse(response, makeRequest);
    };

    return makeRequest();
  }

  /**
   * Generic DELETE request
   */
  async delete(endpoint) {
    const makeRequest = async () => {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });

      return this.handleResponse(response, makeRequest);
    };

    return makeRequest();
  }

  // ==================== Authentication ====================

  /**
   * Login user
   */
  async login(username, password) {
    const response = await fetch(`${this.baseURL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error('Incorrect username or password.');
    }

    if (data.access) {
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      this.token = data.access;
    }
    return data;
  }

  /**
   * Refresh access token
   * Note: This bypasses handleResponse to avoid recursive 401 handling
   */
  async refreshToken() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }
      this.token = data.access;
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Logout user
   */
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    this.token = null;
    window.location.href = '/login';
  }

  /**
   * Change password
   */
  async changePassword(currentPassword, newPassword) {
    return this.post('/auth/change-password/', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  // ==================== AMN API Endpoints ====================

  // Locations
  async getLocations() {
    return this.get('/amn/locations/');
  }

  async getLocation(id) {
    return this.get(`/amn/locations/${id}/`);
  }

  async createLocation(data) {
    return this.post('/amn/locations/', data);
  }

  // Clients
  async getClients(params = {}) {
    return this.get('/amn/clients/', params);
  }

  async getClient(id) {
    return this.get(`/amn/clients/${id}/`);
  }

  async createClient(data) {
    return this.post('/amn/clients/', data);
  }

  async updateClient(id, data) {
    return this.patch(`/amn/clients/${id}/`, data);
  }

  // Staff / Technicians
  async getStaff(params = {}) {
    return this.get('/amn/staff/', params);
  }

  async getTechnicians() {
    return this.get('/amn/staff/technicians/');
  }

  async getStaffMember(id) {
    return this.get(`/amn/staff/${id}/`);
  }

  async createStaff(data) {
    return this.post('/amn/staff/', data);
  }

  async updateStaff(id, data) {
    return this.patch(`/amn/staff/${id}/`, data);
  }

  async getStaffQualifications(id) {
    return this.get(`/amn/staff/${id}/qualifications/`);
  }

  async updateStaffQualifications(id, data) {
    return this.put(`/amn/staff/${id}/qualifications/`, data);
  }

  async getIntakeLink(staffId) {
    return this.get(`/amn/hiring/intake-link/${staffId}/`);
  }

  // Services
  async getServices() {
    return this.get('/amn/services/');
  }

  async createService(data) {
    return this.post('/amn/services/', data);
  }

  async updateService(id, data) {
    return this.patch(`/amn/services/${id}/`, data);
  }

  // Appointments
  async getAppointments(params = {}) {
    return this.get('/amn/appointments/', params);
  }

  async getAppointment(id) {
    return this.get(`/amn/appointments/${id}/`);
  }

  async createAppointment(data) {
    return this.post('/amn/appointments/', data);
  }

  async createAppointmentFull(data) {
    return this.post('/amn/appointments/create-full/', data);
  }

  async updateAppointment(id, data) {
    return this.patch(`/amn/appointments/${id}/`, data);
  }

  async cancelAppointment(id) {
    return this.post(`/amn/appointments/${id}/cancel/`);
  }

  async confirmAppointment(id) {
    return this.post(`/amn/appointments/${id}/confirm/`);
  }

  // Event Technicians
  async createEventTechnician(data) {
    return this.post('/amn/event-technicians/', data);
  }

  async updateEventTechnician(id, data) {
    return this.patch(`/amn/event-technicians/${id}/`, data);
  }

  async deleteEventTechnician(id) {
    return this.delete(`/amn/event-technicians/${id}/`);
  }

  // Transactions
  async getTransactions(params = {}) {
    return this.get('/amn/transactions/', params);
  }

  async getTransactionStats(params = {}) {
    return this.get('/amn/transactions/stats/', params);
  }

  async matchTransaction(id, appointmentId) {
    return this.post(`/amn/transactions/${id}/match_appointment/`, {
      appointment_id: appointmentId
    });
  }

  // Payroll
  async getPayrolls(params = {}) {
    return this.get('/amn/payrolls/', params);
  }

  async getPayroll(id) {
    return this.get(`/amn/payrolls/${id}/`);
  }

  async confirmPayroll(id) {
    return this.post(`/amn/payrolls/${id}/confirm/`);
  }

  async computePayroll(technicianId, week, year) {
    return this.post('/amn/payrolls/compute/', {
      technician_id: technicianId,
      week: parseInt(week),
      year: parseInt(year)
    });
  }

  async computeAllPayroll(week, year) {
    return this.post('/amn/payrolls/compute_all/', {
      week: parseInt(week),
      year: parseInt(year)
    });
  }

  async requestPayrollPayment(payrollId) {
    return this.post(`/amn/payrolls/${payrollId}/request_payment/`);
  }

  async previewPayrollEmail(payrollId) {
    return this.get(`/amn/payrolls/${payrollId}/preview_email/`);
  }

  async sendPayrollEmail(payrollId) {
    return this.post(`/amn/payrolls/${payrollId}/send_email/`);
  }

  async deletePayroll(id) {
    return this.delete(`/amn/payrolls/${id}/`);
  }

  async addPayrollAdjustment(payrollId, data) {
    return this.post(`/amn/payrolls/${payrollId}/add_adjustment/`, data);
  }

  async removePayrollItem(payrollId, itemId) {
    return this.post(`/amn/payrolls/${payrollId}/remove_item/`, { item_id: itemId });
  }

  /**
   * Get 1099 report data for a technician (JSON preview)
   */
  async get1099Report(technicianId, year) {
    return this.get('/amn/payrolls/report_1099/', {
      contractor: technicianId,
      year: year,
      output: 'json'
    });
  }

  /**
   * Download 1099 report as CSV
   * Returns a Blob for file download
   */
  async download1099ReportCSV(technicianId, year) {
    const url = new URL(`${this.baseURL}/amn/payrolls/report_1099/`);
    url.searchParams.append('contractor', technicianId);
    url.searchParams.append('year', year);
    url.searchParams.append('output', 'csv');

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Failed to download report');
    }

    return response.blob();
  }

  // Interactions / Leads
  async getInteractions(params = {}) {
    return this.get('/amn/interactions/', params);
  }

  async resolveInteraction(id) {
    return this.post(`/amn/interactions/${id}/resolve/`);
  }

  async snoozeInteraction(id, snoozeUntil, comment) {
    return this.post(`/amn/interactions/${id}/snooze/`, {
      snooze_until: snoozeUntil,
      snooze_comment: comment
    });
  }

  async markInteractionAsLost(id, lostReason) {
    return this.post(`/amn/interactions/${id}/mark_as_lost/`, {
      lost_reason: lostReason || ''
    });
  }

  async markInteractionAsConverted(id) {
    return this.post(`/amn/interactions/${id}/mark_as_converted/`);
  }

  async updateInteractionTranscript(id, transcriptData) {
    return this.post(`/amn/interactions/${id}/update_transcript/`, {
      transcript: transcriptData
    });
  }

  async toggleOliviaExclude(personId, exclude) {
    return this.post('/amn/interactions/olivia-exclude/', {
      person_id: personId,
      exclude,
    });
  }

  async assignLead(personId, userId) {
    return this.post('/amn/leads/assign/', {
      person_id: personId,
      user_id: userId || null,
    });
  }

  async updateLeadStatus(personId, status, reason = '', snoozeUntil = null) {
    const payload = { person_id: personId, status, reason };
    if (snoozeUntil) payload.snooze_until = snoozeUntil;
    return this.post('/amn/leads/update-status/', payload);
  }

  async updateLeadNote(personId, note) {
    return this.post('/amn/leads/update-note/', {
      person_id: personId,
      note,
    });
  }

  async getOliviaSettings() {
    return this.get('/amn/olivia/settings/');
  }

  async updateOliviaSettings(data) {
    return this.put('/amn/olivia/settings/', data);
  }

  async deleteInteractionTranscript(id) {
    return this.delete(`/amn/interactions/${id}/delete_transcript/`);
  }

  // Technician Days Off / Availability
  async getTechnicianDaysOff(params = {}) {
    return this.get('/amn/technician-days-off/', params);
  }
  async createTechnicianDayOff(data) {
    return this.post('/amn/technician-days-off/', data);
  }
  async toggleTechnicianDayOff(data) {
    return this.post('/amn/technician-days-off/toggle/', data);
  }
  async deleteTechnicianDayOff(id) {
    return this.delete(`/amn/technician-days-off/${id}/`);
  }

  // Messaging
  async getUnreadCounts() {
    return this.get('/amn/messaging/unread/');
  }

  // Technician Calendars
  async getTechnicianCalendar(code) {
    return this.get(`/amn/technician-calendars/by_code/?code=${code}`);
  }

  // Health Check
  async healthCheck() {
    const response = await fetch(`${this.baseURL.replace('/api/v1', '')}/healthz`);
    return response.ok;
  }

  // Metrics
  async getDailyMetrics(days = 30) {
    return this.get('/amn/metrics/daily/', { days });
  }

  async getWeeklyMetrics(weeks = 52) {
    return this.get('/amn/metrics/weekly/', { weeks });
  }

  async getMonthlyMetrics(months = 24) {
    return this.get('/amn/metrics/monthly/', { months });
  }

  async getQuarterlyMetrics(quarters = 10) {
    return this.get('/amn/metrics/quarterly/', { quarters });
  }

  async getDashboardOverview(days = 30) {
    return this.get('/amn/metrics/dashboard/', { days });
  }

  async getWebsiteTraffic({ days, dateFrom, dateTo } = {}) {
    const params = {};
    if (dateFrom && dateTo) {
      params.date_from = dateFrom;
      params.date_to = dateTo;
    } else if (days) {
      params.days = days;
    }
    return this.get('/amn/metrics/website-traffic/', params);
  }

  // Quality Metrics
  async getQualityMetrics(days = 90) {
    return this.get('/amn/metrics/quality/', { days });
  }

  // Requests Metrics (active/snoozed counts)
  async getRequestsMetrics(days = 30) {
    return this.get('/amn/metrics/requests/', { days });
  }

  // P&L (Mercury bank transactions)
  async getPnl(params = {}) {
    return this.get('/amn/metrics/pnl/', params);
  }

  async getPnlMappings() {
    return this.get('/amn/metrics/pnl/mappings/');
  }

  async updatePnlMapping(id, data) {
    return this.patch(`/amn/metrics/pnl/mappings/${id}/`, data);
  }

  // Zip Lookup
  async zipLookup(zip) {
    return this.get('/amn/zip-lookup/', { zip });
  }

  async getZipsServed() {
    return this.get('/amn/zips-served/');
  }

  // Stripe Payments
  async getStripeKey() {
    return this.get('/amn/payments/stripe-key/');
  }

  async createSetupIntent(clientId) {
    return this.post('/amn/payments/setup-intent/', { client_id: clientId });
  }

  async confirmCardSaved(clientId, setupIntentId) {
    return this.post('/amn/payments/confirm-card/', { client_id: clientId, setup_intent_id: setupIntentId });
  }

  async chargeClient(clientId, amount, { description, appointmentId, tip } = {}) {
    return this.post('/amn/payments/charge/', {
      client_id: clientId,
      amount,
      description,
      appointment_id: appointmentId,
      tip,
    });
  }

  async sendCardLink(clientId, method = 'sms') {
    return this.post('/amn/payments/send-card-link/', { client_id: clientId, method });
  }

  // ETA
  async sendEta(appointmentId, message, etaMinutes) {
    return this.post(`/amn/appointments/${appointmentId}/send_eta/`, {
      message,
      eta_minutes: etaMinutes,
    });
  }

  // Outgoing Calls
  async getOutgoingCalls(params = {}) {
    return this.get('/amn/calling/history/', params);
  }

  async getOutgoingCall(id) {
    return this.get(`/amn/calling/history/${id}/`);
  }

  async getCallRecordingUrl(id) {
    return this.get(`/amn/calling/history/${id}/recording/`);
  }

  // Bookings Metrics (daily new bookings from Square)
  async getBookingsMetrics(days = 30, bookedBy = null) {
    const params = { days };
    if (bookedBy) params.booked_by = bookedBy;
    return this.get('/amn/metrics/bookings/', params);
  }

  // Google Reviews
  async getGoogleReviews(params = {}) {
    return this.get('/amn/google-reviews/', params);
  }

  async getGoogleReviewStats() {
    return this.get('/amn/google-reviews/stats/');
  }

  async getGoogleReview(id) {
    return this.get(`/amn/google-reviews/${id}/`);
  }

  async createGoogleReview(data) {
    return this.post('/amn/google-reviews/', data);
  }

  async updateGoogleReview(id, data) {
    return this.patch(`/amn/google-reviews/${id}/`, data);
  }

  async getReviewRequests(params = {}) {
    return this.get('/amn/review-requests/', params);
  }

  // Team Tasks
  async getTeamTasks(params = {}) {
    return this.get('/amn/team-tasks/', params);
  }

  async createTeamTask(data) {
    return this.post('/amn/team-tasks/', data);
  }

  async updateTeamTask(id, data) {
    return this.patch(`/amn/team-tasks/${id}/`, data);
  }

  async deleteTeamTask(id) {
    return this.delete(`/amn/team-tasks/${id}/`);
  }

  // Comm Board
  async getCommItems(params = {}) {
    return this.get('/amn/comm-items/', params);
  }

  async getCommItem(id) {
    return this.get(`/amn/comm-items/${id}/`);
  }

  async createCommItem(data) {
    return this.post('/amn/comm-items/', data);
  }

  async updateCommItem(id, data) {
    return this.patch(`/amn/comm-items/${id}/`, data);
  }

  async deleteCommItem(id) {
    return this.delete(`/amn/comm-items/${id}/`);
  }

  async resolveCommItem(id) {
    return this.post(`/amn/comm-items/${id}/resolve/`);
  }

  async reopenCommItem(id) {
    return this.post(`/amn/comm-items/${id}/reopen/`);
  }

  async getCommCounts() {
    return this.get('/amn/comm-items/counts/');
  }

  async createCommReply(data) {
    return this.post('/amn/comm-replies/', data);
  }

  // Settings (for review automation config)
  async getSettings(params = {}) {
    return this.get('/amn/settings/', params);
  }

  async updateSetting(id, data) {
    return this.patch(`/amn/settings/${id}/`, data);
  }

  // ==================== Tracking System API Endpoints ====================
  // Note: Tracking API doesn't require authentication

  /**
   * Helper to build URL with query params for tracking API
   */
  buildTrackingURL(endpoint, params = {}) {
    const url = new URL(`${TRACKING_API_BASE_URL}${endpoint}`);
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        url.searchParams.append(key, params[key]);
      }
    });
    return url;
  }

  /**
   * Handle tracking API response (no auth required)
   */
  async handleTrackingResponse(response) {
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || data.message || `API request failed with status ${response.status}`);
    }
    return response.json().catch(() => ({}));
  }

  // ==================== Bugs API ====================

  async getBugs(params = {}) {
    const url = this.buildTrackingURL('/bugs', params);
    const response = await fetch(url, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async getBug(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/bugs/${id}`, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async createBug(data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/bugs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async updateBug(id, data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/bugs/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async deleteBug(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/bugs/${id}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      return { success: true };
    }
    throw new Error('Failed to delete bug');
  }

  async getBugStats() {
    const response = await fetch(`${TRACKING_API_BASE_URL}/bugs/stats/summary`, {
      method: 'GET',
    });
    return this.handleTrackingResponse(response);
  }

  // ==================== Features API ====================

  async getFeatures(params = {}) {
    const url = this.buildTrackingURL('/features', params);
    const response = await fetch(url, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async getFeature(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/features/${id}`, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async createFeature(data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/features`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async updateFeature(id, data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/features/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async deleteFeature(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/features/${id}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      return { success: true };
    }
    throw new Error('Failed to delete feature');
  }

  async getFeatureStats() {
    const response = await fetch(`${TRACKING_API_BASE_URL}/features/stats/summary`, {
      method: 'GET',
    });
    return this.handleTrackingResponse(response);
  }

  // ==================== Ideas API ====================

  async getIdeas(params = {}) {
    const url = this.buildTrackingURL('/ideas', params);
    const response = await fetch(url, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async getIdea(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/ideas/${id}`, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async createIdea(data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/ideas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async updateIdea(id, data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/ideas/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async deleteIdea(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/ideas/${id}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      return { success: true };
    }
    throw new Error('Failed to delete idea');
  }

  async getIdeaStats() {
    const response = await fetch(`${TRACKING_API_BASE_URL}/ideas/stats/summary`, {
      method: 'GET',
    });
    return this.handleTrackingResponse(response);
  }

  // ==================== Research API ====================

  async getResearch(params = {}) {
    const url = this.buildTrackingURL('/research', params);
    const response = await fetch(url, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async getResearchItem(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/research/${id}`, { method: 'GET' });
    return this.handleTrackingResponse(response);
  }

  async createResearch(data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/research`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async updateResearch(id, data) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/research/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return this.handleTrackingResponse(response);
  }

  async deleteResearch(id) {
    const response = await fetch(`${TRACKING_API_BASE_URL}/research/${id}`, {
      method: 'DELETE',
    });
    if (response.ok) {
      return { success: true };
    }
    throw new Error('Failed to delete research');
  }

  async getResearchStats() {
    const response = await fetch(`${TRACKING_API_BASE_URL}/research/stats/summary`, {
      method: 'GET',
    });
    return this.handleTrackingResponse(response);
  }

  // ==================== Targets API ====================

  async getTargets(params = {}) {
    return this.get('/amn/targets/', params);
  }

  // ==================== Jobs/Celery Tasks API ====================

  async getJobs(params = {}) {
    return this.get('/amn/jobs/', params);
  }

  async getJob(id) {
    return this.get(`/amn/jobs/${id}/`);
  }

  async getJobStats() {
    return this.get('/amn/jobs/stats/');
  }

  async getScheduledTasks() {
    return this.get('/amn/jobs/scheduled_tasks/');
  }

  async runTask(taskName) {
    return this.post('/amn/jobs/run_task/', { task_name: taskName });
  }

  // ==================== Follow-Up Pipeline API ====================

  async getFollowUpPipeline(params = {}) {
    return this.get('/amn/clients/followup_pipeline/', params);
  }

  async getTodaysFollowUps(params = {}) {
    return this.get('/amn/clients/todays_followups/', params);
  }

  async getFollowUpCount() {
    return this.get('/amn/clients/followup_count/');
  }

  async updateFollowUp(clientId, data) {
    return this.post(`/amn/clients/${clientId}/update_followup/`, data);
  }

  // Data Quality
  async getDataQualityReport() {
    return this.get('/amn/data-quality/');
  }

  // ==================== Hiring / Candidates ====================

  async getCandidates(params = {}) {
    return this.get('/amn/candidates/', params);
  }

  async getCandidate(id) {
    return this.get(`/amn/candidates/${id}/`);
  }

  async createCandidate(data) {
    return this.post('/amn/candidates/', data);
  }

  async updateCandidate(id, data) {
    return this.patch(`/amn/candidates/${id}/`, data);
  }

  async transitionCandidate(id, data) {
    return this.post(`/amn/candidates/${id}/transition/`, data);
  }

  async beginOnboarding(id) {
    return this.post(`/amn/candidates/${id}/begin_onboarding/`);
  }

  // Job Postings
  async getJobPostings(params = {}) {
    return this.get('/amn/job-postings/', params);
  }

  async getJobPosting(id) {
    return this.get(`/amn/job-postings/${id}/`);
  }

  async createJobPosting(data) {
    return this.post('/amn/job-postings/', data);
  }

  async updateJobPosting(id, data) {
    return this.patch(`/amn/job-postings/${id}/`, data);
  }

  async closeJobPosting(id) {
    return this.post(`/amn/job-postings/${id}/close/`);
  }

  // Candidate Notes
  async getCandidateNotes(candidateId) {
    return this.get('/amn/candidate-notes/', { candidate: candidateId });
  }

  async createCandidateNote(data) {
    return this.post('/amn/candidate-notes/', data);
  }

  async getCandidateLicenseImage(id) {
    return this.get(`/amn/candidates/${id}/license-image/`);
  }

  // Hiring Metrics
  async getHiringMetrics() {
    return this.get('/amn/hiring/metrics/');
  }

  // Staff Square Linking
  async linkStaffSquare(staffId, squareId) {
    return this.post(`/amn/staff/${staffId}/link_square/`, { square_id: squareId });
  }

  // Gift Cards
  async getGiftCards(params = {}) {
    return this.get('/amn/gift-card-purchases/', params);
  }

  async getGiftCard(id) {
    return this.get(`/amn/gift-card-purchases/${id}/`);
  }

  async redeemGiftCard(id, data) {
    return this.post(`/amn/gift-card-purchases/${id}/redeem/`, data);
  }

  // Online Bookings
  async getOnlineBookings(params = {}) {
    return this.get('/amn/online-bookings/', params);
  }

  async getOnlineBookingStats() {
    return this.get('/amn/online-bookings/stats/');
  }

  async getOnlineBooking(id) {
    return this.get(`/amn/online-bookings/${id}/`);
  }

  async updateOnlineBooking(id, data) {
    return this.patch(`/amn/online-bookings/${id}/`, data);
  }

  // Online booking ↔ Appointment linking
  async linkBookingToAppointment(appointmentId, bookingId) {
    return this.post(`/amn/appointments/${appointmentId}/link-booking/`, { booking_id: bookingId });
  }

  async unlinkBookingFromAppointment(appointmentId) {
    return this.post(`/amn/appointments/${appointmentId}/unlink-booking/`, {});
  }

  async getUnlinkedBookings(appointmentId) {
    return this.get(`/amn/appointments/${appointmentId}/unlinked-bookings/`);
  }

  async chargeBalance(appointmentId, { tip } = {}) {
    return this.post(`/amn/appointments/${appointmentId}/charge-balance/`, { tip: tip || 0 });
  }

  // ==================== Gift Card Redemption ====================

  async applyGiftCard(appointmentId, giftCardId) {
    return this.post(`/amn/appointments/${appointmentId}/apply-gift-card/`, { gift_card_id: giftCardId });
  }

  async removeGiftCard(appointmentId, giftCardId) {
    return this.post(`/amn/appointments/${appointmentId}/remove-gift-card/`, { gift_card_id: giftCardId });
  }

  async getUnredeemedGiftCards() {
    return this.get('/amn/appointments/unredeemed-gift-cards/');
  }

  // ==================== ALF Facilities ====================

  async getAlfFacilities(params = {}) {
    return this.get('/amn/alf-facilities/', params);
  }

  async getAlfFacility(id) {
    return this.get(`/amn/alf-facilities/${id}/`);
  }

  async createAlfFacility(data) {
    return this.post('/amn/alf-facilities/', data);
  }

  async updateAlfFacility(id, data) {
    return this.patch(`/amn/alf-facilities/${id}/`, data);
  }

  async deleteAlfFacility(id) {
    return this.delete(`/amn/alf-facilities/${id}/`);
  }

  // ALF Facility Contacts
  async getAlfFacilityContacts(facilityId) {
    return this.get('/amn/alf-facility-contacts/', { facility: facilityId });
  }

  async createAlfFacilityContact(data) {
    return this.post('/amn/alf-facility-contacts/', data);
  }

  async updateAlfFacilityContact(id, data) {
    return this.patch(`/amn/alf-facility-contacts/${id}/`, data);
  }

  async deleteAlfFacilityContact(id) {
    return this.delete(`/amn/alf-facility-contacts/${id}/`);
  }

  // ==================== Email Campaigns ====================

  async getCampaigns(params = {}) {
    return this.get('/amn/campaigns/', params);
  }

  async getCampaign(id) {
    return this.get(`/amn/campaigns/${id}/`);
  }

  async createCampaign(data) {
    return this.post('/amn/campaigns/', data);
  }

  async updateCampaign(id, data) {
    return this.patch(`/amn/campaigns/${id}/`, data);
  }

  async deleteCampaign(id) {
    return this.delete(`/amn/campaigns/${id}/`);
  }

  async sendCampaign(id) {
    return this.post(`/amn/campaigns/${id}/send/`);
  }

  async scheduleCampaign(id, scheduledAt) {
    return this.post(`/amn/campaigns/${id}/schedule/`, { scheduled_at: scheduledAt });
  }

  async cancelCampaign(id) {
    return this.post(`/amn/campaigns/${id}/cancel/`);
  }

  async duplicateCampaign(id) {
    return this.post(`/amn/campaigns/${id}/duplicate/`);
  }

  async previewCampaign(id, email) {
    return this.post(`/amn/campaigns/${id}/preview/`, { email });
  }

  async getCampaignRecipients(id, params = {}) {
    return this.get(`/amn/campaigns/${id}/recipients/`, params);
  }

  async resolveCampaignAudience(id) {
    return this.get(`/amn/campaigns/${id}/resolve_audience/`);
  }

  async getCampaignStats() {
    return this.get('/amn/campaigns/stats/');
  }

  // ==================== Subscribers ====================

  async getSubscribers(params = {}) {
    return this.get('/amn/subscribers/', params);
  }

  async getSubscriber(id) {
    return this.get(`/amn/subscribers/${id}/`);
  }

  async createSubscriber(data) {
    return this.post('/amn/subscribers/', data);
  }

  async updateSubscriber(id, data) {
    return this.patch(`/amn/subscribers/${id}/`, data);
  }

  async deleteSubscriber(id) {
    return this.delete(`/amn/subscribers/${id}/`);
  }

  async getSubscriberStats() {
    return this.get('/amn/subscribers/stats/');
  }

  async importSubscribersCsv(formData) {
    return this.post('/amn/subscribers/import_csv/', formData, true);
  }

  async bulkTagSubscribers(data) {
    return this.post('/amn/subscribers/bulk_tag/', data);
  }

  // ==================== Tags ====================

  async getTags(params = {}) {
    return this.get('/amn/tags/', params);
  }

  async createTag(data) {
    return this.post('/amn/tags/', data);
  }

  async updateTag(id, data) {
    return this.patch(`/amn/tags/${id}/`, data);
  }

  async deleteTag(id) {
    return this.delete(`/amn/tags/${id}/`);
  }

  // ==================== Email Templates ====================

  async getEmailTemplates(params = {}) {
    return this.get('/amn/email-templates/', params);
  }

  async getEmailTemplate(id) {
    return this.get(`/amn/email-templates/${id}/`);
  }

  async createEmailTemplate(data) {
    return this.post('/amn/email-templates/', data);
  }

  async updateEmailTemplate(id, data) {
    return this.patch(`/amn/email-templates/${id}/`, data);
  }

  async deleteEmailTemplate(id) {
    return this.delete(`/amn/email-templates/${id}/`);
  }

  async duplicateEmailTemplate(id) {
    return this.post(`/amn/email-templates/${id}/duplicate/`);
  }

  async renderEmailTemplate(id, blocks) {
    return this.post(`/amn/email-templates/${id}/render/`, { blocks });
  }

  async renderCampaignPreview(id, blocks) {
    return this.post(`/amn/campaigns/${id}/render_preview/`, { blocks });
  }

  async getRevenueByLocation(params = {}) {
    return this.get('/amn/metrics/revenue-by-location/', params);
  }

  async generateImage(prompt, size = '1792x1024') {
    return this.post('/amn/campaigns/generate-image/', { prompt, size });
  }

}

// Export singleton instance
const apiService = new ApiService();
export default apiService;
